import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Star, Sparkles } from "lucide-react";

const KEY = (uid, tab) => `rk_vault_seen_${uid}_${tab}`;

// Snapshot {id: level} of owned items per tab; returns popups for new / leveled items since last visit.
export function diffVaultTab(uid, tab, rows) {
  if (!uid) return [];
  const now = {};
  rows.forEach((r) => { now[r.id] = r.level; });
  const raw = localStorage.getItem(KEY(uid, tab));
  localStorage.setItem(KEY(uid, tab), JSON.stringify(now));
  if (!raw) return [];
  let prev = {};
  try { prev = JSON.parse(raw) || {}; } catch { return []; }
  const out = [];
  rows.forEach((r) => {
    if (!(r.id in prev)) out.push({ ...r, kind: "new" });
    else if (r.levels && r.level > prev[r.id]) out.push({ ...r, kind: "up" });
  });
  return out;
}

const RAYS = Array.from({ length: 12 }, (_, i) => i * 30);

function Pop({ it, fast, onDone }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    const t = setTimeout(onDone, fast ? 750 : 1300);
    return () => clearTimeout(t);
  }, [onDone, fast]);
  const isNew = it.kind === "new";
  const c = it.color || "#FFE600";
  return (
    <div className="starup-card relative flex flex-col items-center" data-testid="starup-popup-card" style={{ "--su": c, animationDuration: fast ? "0.75s" : "1.3s" }}>
      <div className="absolute inset-0 -m-24 pointer-events-none">
        {RAYS.map((deg) => (
          <Star key={deg} size={18} fill={c} className="starup-ray absolute left-1/2 top-1/2" style={{ color: c, "--deg": `${deg}deg`, filter: `drop-shadow(0 0 6px ${c})` }} />
        ))}
      </div>
      <div className="starup-label font-graffiti text-2xl mb-2" data-testid="starup-popup-kind"
           style={{ color: isNew ? "#39FF14" : "#FFE600", textShadow: `0 0 14px ${isNew ? "#39FF14" : "#FFE600"}, 2px 2px 0 #000` }}>
        {isNew ? "NEW!" : "STAR UP!"}
      </div>
      <div className="w-36 h-36 rounded-2xl overflow-hidden border-[3px] bg-[#0A051A]" style={{ borderColor: c, boxShadow: `0 0 30px ${c}, 0 0 70px ${c}66` }}>
        {it.image && !broken ? <img src={it.image} alt={it.name} className="w-full h-full object-cover" data-testid="starup-popup-image" onError={() => setBroken(true)} />
                  : <div className="w-full h-full flex items-center justify-center"><Sparkles size={48} style={{ color: c }} /></div>}
      </div>
      <div className="mt-2 font-graffiti text-xl text-white text-center max-w-[260px] truncate" data-testid="starup-popup-name" style={{ textShadow: `0 0 10px ${c}, 2px 2px 0 #000` }}>{it.name}</div>
      {it.levels && (
        <div className="flex gap-0.5 mt-1" data-testid="starup-popup-stars">
          {Array.from({ length: it.max || it.level }).map((_, i) => (
            <Star key={i} size={14} fill={i < it.level ? "#FFE600" : "transparent"} className={i === it.level - 1 && !isNew ? "starup-newstar" : ""}
                  style={{ color: i < it.level ? "#FFE600" : "#4A3470", filter: i < it.level ? "drop-shadow(0 0 4px #FFE600)" : "none" }} />
          ))}
        </div>
      )}
    </div>
  );
}

const MAX_POPS = 10;

export function StarUpPopup({ queue: fullQueue, onClear }) {
  const queue = useMemo(() => fullQueue.slice(0, MAX_POPS), [fullQueue]);
  const extra = fullQueue.length - queue.length;
  const [idx, setIdx] = useState(0);
  useEffect(() => { setIdx(0); }, [queue]);
  const fast = queue.length > 2;
  const next = useMemo(() => () => setIdx((i) => i + 1), []);
  useEffect(() => { if (queue.length && idx >= queue.length) onClear(); }, [idx, queue, onClear]);
  if (!queue.length || idx >= queue.length) return null;
  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center cursor-pointer" data-testid="starup-popup" onClick={onClear}
         style={{ background: "radial-gradient(circle, rgba(10,5,26,0.55), rgba(10,5,26,0.15) 60%, transparent)" }}>
      <Pop key={`${queue[idx].id}-${idx}`} it={queue[idx]} fast={fast} onDone={next} />
      {queue.length > 1 && (
        <div className="absolute bottom-10 font-mono text-[10px] uppercase tracking-[0.3em] text-[#C9BDF0]" data-testid="starup-popup-counter">
          {idx + 1} / {queue.length}{extra > 0 ? ` (+${extra} more)` : ""} · tap to skip
        </div>
      )}
    </div>,
    document.body
  );
}
